
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const CAGRCalculator = () => {
  const [initialValue, setInitialValue] = useState<string>('10000');
  const [finalValue, setFinalValue] = useState<string>('15000');
  const [timePeriod, setTimePeriod] = useState<string>('3');
  const [cagr, setCAGR] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateCAGR = () => {
    try {
      setError(null);
      
      const initial = parseFloat(initialValue);
      const final = parseFloat(finalValue);
      const years = parseFloat(timePeriod);

      if (isNaN(initial) || isNaN(final) || isNaN(years)) {
        setError("Please enter valid numbers");
        return;
      }

      if (initial <= 0 || final <= 0 || years <= 0) {
        setError("All values must be positive");
        return;
      }

      // CAGR = (Ending Value / Beginning Value)^(1/n) - 1
      const cagrValue = (Math.pow(final / initial, 1 / years) - 1) * 100;
      setCAGR(cagrValue);

    } catch (err) {
      setError("Error calculating CAGR");
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">CAGR (Compound Annual Growth Rate) Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="initial-value">Initial Value ($)</Label>
              <Input
                id="initial-value"
                type="number"
                value={initialValue}
                onChange={(e) => setInitialValue(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="final-value">Final Value ($)</Label>
              <Input
                id="final-value"
                type="number"
                value={finalValue}
                onChange={(e) => setFinalValue(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="time-period">Time Period (years)</Label>
              <Input
                id="time-period"
                type="number"
                step="0.1"
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                className="mt-1"
              />
            </div>

            <Button onClick={calculateCAGR} className="w-full">
              Calculate CAGR
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {cagr !== null && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium">Compound Annual Growth Rate</h3>
                <p className="text-2xl font-bold">{cagr.toFixed(2)}%</p>
                <p className="text-sm text-gray-600 mt-2">
                  Your investment grew at an average annual rate of {cagr.toFixed(2)}%
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CAGRCalculator;
